import { Link, useNavigate } from "react-router-dom";
import {
  Breadcrumb, Card, Form, Row, Col, Button
} from "react-bootstrap";
import {
  Page,
  Spinner,
  IconButton,
  IconButtonType,
  Divider,
  ErrorMessages,
  NoData
} from "@whitespace/whitespace-ui";
import { DropDownTreeComponent } from "@syncfusion/ej2-react-dropdowns";
import { ariaInvalidCheck } from "@whitespace/whitespace-utils";
import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useStateMachine } from "little-state-machine";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import IRegion from "interfaces/IRegion";
import { makeDropDownTreeAACompliant } from "modules/SyncfusionAAFixer";
import "styles/form.scss";
import "styles/form-mixins.scss";
import { normaliseLatLong } from "modules/Normalise";
import INonWorkingPeriod from "interfaces/INonWorkingPeriod";
import IUpdateDisposalSiteRequestModel from "interfaces/Request/IUpdateDisposalSiteRequestModel";
import disposalSiteSchema from "modules/schemas/disposalSiteSchema";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";

const UpdateDisposalSite = () => {
  const { state } = useStateMachine();
  const [disposalSite, setDisposalSite] = useState<
  IUpdateDisposalSiteRequestModel | undefined
  >(state.editdisposalSiteRequest);
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [updateButtonDisabled, setUpdateButtonDisabled] = useState<boolean>(true);
  const [isSending, setIsSending] = useState<boolean>(true);
  const [regionsDropDownTreeComponent, setRegionsDropDownTreeComponent] = useState<DropDownTreeComponent>();
  const [nwpDropDownTreeComponent, setNwpDropDownTreeComponent] = useState<DropDownTreeComponent>();
  const [regions, setRegions] = useState<IRegion[] | undefined>();
  const [selectedRegions, setSelectedRegions] = useState<string[] | undefined>(
    disposalSite?.regionIds
  );
  const [nonWorkingPeriods, setNonWorkingPeriods] = useState<
  INonWorkingPeriod[] | undefined
  >();
  const [selectedNonWorkingPeriods, setSelectedNonWorkingPeriods] = useState<
  string[] | undefined
  >(disposalSite?.nonWorkingPeriodIds);

  const [regionsDataBoundComplete, setRegionsDataBoundComplete] = useState<boolean>(false);
  const [nonWorkingPeriodDataBoundComplete, setNonWorkingPeriodDataBoundComplete] = useState<boolean>(false);
  const [aaFixDropDownTreeCompleted, setAAFixDropDownTreeCompleted] = useState<boolean>(false);

  const regionsFields: Object = {
    dataSource: regions,
    value: "id",
    text: "name",
    child: "nodeChild"
  };

  const nonWorkingPeriodsFields: Object = {
    dataSource: nonWorkingPeriods,
    value: "id",
    text: "name",
    child: "nodeChild"
  };

  const regionsTreeSettings = { autoCheck: false };

  const nonWorkingPeriodTreeSettings = { autoCheck: false };

  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    watch
  } = useForm<IUpdateDisposalSiteRequestModel>({
    resolver: yupResolver(disposalSiteSchema),
    shouldFocusError: true,
    reValidateMode: "onChange"
  });

  const watchFields = watch([
    "name",
    "regionIds",
    "latitude",
    "longitude",
    "nonWorkingPeriodIds",
    "active"
  ]);

  useEffect(() => {
    setDisposalSite(state.editdisposalSiteRequest);
  }, [state.editdisposalSiteRequest]);

  useEffect(() => {
    setSelectedRegions(disposalSite?.regionIds);
  }, [disposalSite, setSelectedRegions]);

  useEffect(() => {
    setSelectedNonWorkingPeriods(disposalSite?.nonWorkingPeriodIds);
  }, [disposalSite, setSelectedNonWorkingPeriods]);

  useEffect(() => {
    if (disposalSite) {
      const name = getValues("name");
      const latitude = getValues("latitude") ?? "";
      const longitude = getValues("longitude") ?? "";
      const active = getValues("active");

      const regionsRemoved = disposalSite?.regionIds?.filter(
        (x: any) => !selectedRegions?.includes(x)
      ) ?? [];
      const regionsAdded = selectedRegions?.filter(
        (x: any) => !disposalSite?.regionIds?.includes(x)
      ) ?? [];

      const nwpRemoved = disposalSite?.nonWorkingPeriodIds?.filter(
        (x: any) => !selectedNonWorkingPeriods?.includes(x)
      ) ?? [];
      const nwpAdded = selectedNonWorkingPeriods?.filter(
        (x: any) => !disposalSite?.nonWorkingPeriodIds?.includes(x)
      ) ?? [];

      const requiredFieldsEntered = name.length > 0 && selectedRegions && selectedRegions?.length > 0;

      const atLeastOneFieldChangedValue = name !== disposalSite.name
        || regionsRemoved?.length > 0
        || regionsAdded?.length > 0
        || nwpRemoved?.length > 0
        || nwpAdded?.length > 0
        || (disposalSite.latitude
          ? latitude !== disposalSite.latitude?.toString()
          : !disposalSite.latitude?.toString() && latitude.length > 0)
        || (disposalSite.longitude
          ? longitude !== disposalSite.longitude?.toString()
          : !disposalSite.longitude?.toString() && longitude.length > 0)
        || active !== disposalSite.active;

      setUpdateButtonDisabled(
        !(requiredFieldsEntered && atLeastOneFieldChangedValue)
      );
    }
  }, [watchFields, disposalSite]);

  const navigate = useNavigate();

  const {
    isSending: getRegionsIsSending,
    errors: getRegionsErrors,
    data: getRegionsData,
    sendRequest: getRegionsSendRequest
  } = useAmplifyREST();

  const {
    isSending: getNonWorkingPeriodsIsSending,
    errors: getNonWorkingPeriodsErrors,
    data: getNonWorkingPeriodsData,
    sendRequest: getNonWorkingPeriodsSendRequest
  } = useAmplifyREST();

  const {
    isSending: updateDisposalSiteIsSending,
    errors: updateDisposalSiteErrors,
    statusCode: updateDisposalSiteStatusCode,
    sendRequest: updateDisposalSiteSendRequest
  } = useAmplifyREST();

  const apiErrors = getRegionsErrors || updateDisposalSiteErrors || getNonWorkingPeriodsErrors;

  useEffect(() => {
    setIsSending(
      getRegionsIsSending
        || updateDisposalSiteIsSending
        || getNonWorkingPeriodsIsSending
        || !aaFixDropDownTreeCompleted
    );
  }, [
    getRegionsIsSending,
    updateDisposalSiteIsSending,
    getNonWorkingPeriodsIsSending,
    aaFixDropDownTreeCompleted
  ]);

  useEffect(() => {
    if (regionsDataBoundComplete && nonWorkingPeriodDataBoundComplete) {
      makeDropDownTreeAACompliant();
      setAAFixDropDownTreeCompleted(true);
    }
  }, [regionsDataBoundComplete, nonWorkingPeriodDataBoundComplete]);

  useEffect(() => {
    getRegionsSendRequest("settings/regions", "GET");
  }, [getRegionsSendRequest]);

  useEffect(() => {
    if (getRegionsData) {
      setRegions(getRegionsData.regions);
    }
  }, [getRegionsData]);

  useEffect(() => {
    getNonWorkingPeriodsSendRequest("settings/nonworkingperiods", "GET");
  }, [getNonWorkingPeriodsSendRequest]);

  useEffect(() => {
    if (getNonWorkingPeriodsData) {
      setNonWorkingPeriods(getNonWorkingPeriodsData.nonWorkingPeriods);
    }
  }, [getNonWorkingPeriodsData]);

  useEffect(() => {
    if (apiErrors) {
      setShowExceptions(true);
      setAAFixDropDownTreeCompleted(true);
    }
  }, [apiErrors]);

  const regionsDropDownTreeComponentOnChange = () => {
    setSelectedRegions(regionsDropDownTreeComponent?.value);
  };

  const nonWorkingPeriodsDropDownTreeComponentOnChange = () => {
    setSelectedNonWorkingPeriods(nwpDropDownTreeComponent?.value);
  };

  const handleAddDisposalSiteClick = (
    request: IUpdateDisposalSiteRequestModel
  ) => {
    request.latitude = request.latitude!.length > 0 ? request.latitude : undefined;
    request.longitude = request.longitude!.length > 0 ? request.longitude : undefined;
    request.regionIds = selectedRegions!;
    request.nonWorkingPeriodIds = selectedNonWorkingPeriods!;
    updateDisposalSiteSendRequest(
      `settings/disposalsites/${disposalSite?.id}`,
      "PUT",
      request
    );
  };

  useEffect(() => {
    if (updateDisposalSiteStatusCode === 200) navigate(RoutePathsEnum.SettingsDisposalSites);
  }, [updateDisposalSiteStatusCode]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="disposalsite-title">
              Edit Disposal Site
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </div>
        </Card.Header>
        <Form
          autoComplete="off"
          onSubmit={handleSubmit(handleAddDisposalSiteClick)}
        >
          <Card.Body className="no-padding">
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb
                  className="left-margin-large no-wrap"
                  test-id="breadcrumb"
                >
                  <Breadcrumb.Item
                    data-testid="breadcrumb-settings"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.Settings }}
                    onClick={() => navigate(RoutePathsEnum.Settings)}
                  >
                    Settings
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    data-testid="breadcrumb-disposalsites"
                    linkAs={Link}
                    linkProps={{ to: RoutePathsEnum.SettingsDisposalSites }}
                    onClick={() => navigate(RoutePathsEnum.SettingsDisposalSites)}
                  >
                    Disposal Sites
                  </Breadcrumb.Item>
                  <Breadcrumb.Item
                    data-testid="breadcrumb-edit-disposalsite"
                    active
                  >
                    {`Edit ${disposalSite?.name} Disposal Site`}
                  </Breadcrumb.Item>
                </Breadcrumb>
              </Col>
              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={updateButtonDisabled || isSending}
                  aria-disabled={updateButtonDisabled || isSending}
                  aria-label="Update Disposal Site"
                  type="submit"
                  spin={updateDisposalSiteIsSending}
                  data-testid="update"
                >
                  Update
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={() => navigate(RoutePathsEnum.SettingsDisposalSites)}
                  data-testid="cancel"
                  aria-label="Cancel update and back to disposal Sites"
                >
                  Cancel
                </Button>
              </Col>
            </Row>
            <Divider />
            <section className="disposalsites pt-2">
              <Row className="d-flex mt-1 ms-1">
                <Col xs={12}>
                  <div className="control-pane">
                    <div className="control-section" />
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="name">
                        <Form.Label
                          id="siteNameLabel"
                          className="horiz-form-label"
                        >
                          Site Name:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            defaultValue={disposalSite?.name}
                            aria-required="true"
                            aria-invalid={ariaInvalidCheck(
                              "name",
                              errors,
                              null
                            )}
                            aria-labelledby="siteNameLabel"
                            maxLength={50}
                            {...register("name")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="name"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="regionIds">
                        <Form.Label
                          id="regionIdsLabel"
                          className="horiz-form-label"
                        >
                          Region(s):
                        </Form.Label>
                        <Col>
                          {regions && (
                            <DropDownTreeComponent
                              {...register("regionIds")}
                              ref={(d: DropDownTreeComponent) => {
                                setRegionsDropDownTreeComponent(d);
                              }}
                              change={regionsDropDownTreeComponentOnChange}
                              dataBound={() => setRegionsDataBoundComplete(true)}
                              treeSettings={regionsTreeSettings}
                              fields={regionsFields}
                              allowMultiSelection
                              showCheckBox
                              changeOnBlur={false}
                              mode="Box"
                              id="regionIdsDropDown"
                              value={disposalSite?.regionIds}
                              htmlAttributes={{
                                "data-testid": "ddlregionIds",
                                "aria-required": "true"
                              }}
                            />
                          )}
                          <NoData
                            visible={
                              !getRegionsErrors
                              && !getRegionsIsSending
                              && !regions
                            }
                            description="No Regions Found"
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="regions"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="latitude">
                        <Form.Label
                          id="latitudeLabel"
                          className="horiz-form-label"
                        >
                          Latitude:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            {...register("latitude", {
                              onChange: e => {
                                e.target.value = normaliseLatLong(
                                  e.target.value
                                );
                              },
                              onBlur: e => {}
                            })}
                            type="text"
                            defaultValue={disposalSite?.latitude?.toString()}
                            role="textbox"
                            aria-required="false"
                            aria-invalid={ariaInvalidCheck(
                              "latitude",
                              errors,
                              null
                            )}
                            aria-labelledby="latitudeLabel"
                            maxLength={50}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="latitude"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="longitude">
                        <Form.Label
                          id="longitudeLabel"
                          className="horiz-form-label"
                        >
                          Longitude:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            {...register("longitude", {
                              onChange: e => {
                                e.target.value = normaliseLatLong(
                                  e.target.value
                                );
                              },
                              onBlur: e => {}
                            })}
                            defaultValue={disposalSite?.longitude?.toString()}
                            type="text"
                            role="textbox"
                            aria-required="false"
                            aria-invalid={ariaInvalidCheck(
                              "longitude",
                              errors,
                              null
                            )}
                            aria-labelledby="longitudeLabel"
                            maxLength={50}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="longitude"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="nonWorkingPeriodIds">
                        <Form.Label
                          id="nonWorkingPeriodIdsLabel"
                          className="horiz-form-label"
                        >
                          Non-Working Periods:
                        </Form.Label>
                        <Col>
                          {nonWorkingPeriods && (
                            <DropDownTreeComponent
                              {...register("nonWorkingPeriodIds")}
                              ref={(d: DropDownTreeComponent) => {
                                setNwpDropDownTreeComponent(d);
                              }}
                              change={
                                nonWorkingPeriodsDropDownTreeComponentOnChange
                              }
                              treeSettings={nonWorkingPeriodTreeSettings}
                              fields={nonWorkingPeriodsFields}
                              allowMultiSelection
                              showCheckBox
                              changeOnBlur={false}
                              dataBound={() => setNonWorkingPeriodDataBoundComplete(true)}
                              mode="Box"
                              id="nonWorkingPeriodsDropDown"
                              value={disposalSite?.nonWorkingPeriodIds}
                              htmlAttributes={{
                                "aria-required": "true"
                              }}
                            />
                          )}
                          <NoData
                            visible={
                              !getNonWorkingPeriodsErrors
                              && !getNonWorkingPeriodsIsSending
                              && !nonWorkingPeriods
                            }
                            description="No Non-working Periods Found"
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="nonWorkingPeriodIds"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2 mt-3">
                      <Form.Group
                        as={Row}
                        controlId="active"
                        className="d-flex align-items-center"
                      >
                        <Form.Label
                          id="activeLabel"
                          className="d-inline-flex horiz-form-label w-auto mb-0 me-3"
                        >
                          Active:
                        </Form.Label>
                        <Form.Check
                          {...register("active")}
                          defaultChecked={disposalSite?.active}
                          type="switch"
                          className="d-inline-flex custom-switch custom-switch-md ms-2 w-auto"
                          aria-required="true"
                          aria-labelledby="activeLabel"
                        />
                        <ErrorMessages
                          fieldName="active"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                  </div>
                </Col>
              </Row>
            </section>
          </Card.Body>
        </Form>
      </Card>
    </Page>
  );
};

export default UpdateDisposalSite;
